import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';

import AddressLookup from 'views/site/AddressLookup.js';
import Appointment from 'files/Appointment.js';
import AppointmentPicker from 'views/site/AppointmentPicker.js';
import CalendlyScheduler from 'views/site/CalendlyScheduler.js';
import Icon from 'views/site/Icon.js';
import LeadQuiz from 'files/LeadQuiz.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * LeadQuiz — the blind spot checker
 *
 * Somebody arriving from an ad has seen one image and one line of text. They
 * have not decided they want a safety network and they will not read a page to
 * find out. So this asks, and then it tells: every question is answerable from
 * the kitchen table with no homework, and the moment it is answered the thing
 * they did not know appears underneath it. That is the rhythm of the in-home
 * presentation, and it is doing the same job here.
 *
 * The ending is not a result. It is an hour with somebody who will walk the
 * house — the free evaluation is where coverage is decided against a real
 * layout and a real budget, and this exists to make that hour look worth
 * taking.
 */

// Reserved on both sides of the scrolling body so a full-width field's border
// and focus ring are not clipped by the scrollport. Matches the ring width.
const SCROLL_GUTTER = 4;

// Browser autofill reads these rather than guessing from the label, which is
// how the phone number was being skipped.
const AUTOCOMPLETE = {
    email: 'email',
    firstName: 'given-name',
    lastName: 'family-name',
    phone: 'tel'
};

const LeadQuizView = ({ dealership, onClose, scrollRef, utils }) => {

    const mobile = Utils.isMobile();
    const dealerName = dealership?.name || 'your local dealer';
    const steps = LeadQuiz.STEPS;
    const FORM_INDEX = steps.length + 1;
    const SCHEDULE_INDEX = steps.length + 2;

    const [answers, setAnswers] = useState(() => LeadQuiz.getSession().answers);
    const [confirmation, setConfirmation] = useState(null);
    const [customer, setCustomer] = useState({ address: null, email: '', firstName: '', lastName: '', phone: '' });
    const [error, setError] = useState(null);

    // The contact details do not survive the modal closing, and the calendar
    // needs them, so a walkthrough left on it resumes at the details form.
    // A Calendly dealer never shows the details form, and its calendar needs
    // nothing from it, so that walkthrough can resume on the calendar itself.
    const [index, setIndex] = useState(() => Math.min(LeadQuiz.getSession().index, Appointment.getCalendly(dealership) ? SCHEDULE_INDEX : FORM_INDEX));
    const [selected, setSelected] = useState(null);
    const [sending, setSending] = useState(false);
    const [attempted, setAttempted] = useState(false);

    const calendly = useMemo(() => Appointment.getCalendly(dealership), [dealership]);

    // Read on every render rather than once, because a visitor can sit on the
    // quiz long enough for this morning's times to pass. Calendly dealers
    // never see this list.
    const days = calendly ? [] : Appointment.getOpenDays(dealership);
    const canSchedule = calendly !== null || days.length > 0;

    // the walkthrough survives the modal closing - see the note in files/LeadQuiz
    useEffect(() => {
        LeadQuiz.saveSession({ answers, index });
    }, [answers, index]);

    const step = index >= 0 && index < steps.length ? steps[index] : null;
    const showingResult = index === steps.length;
    const showingForm = index === FORM_INDEX;
    const showingSchedule = index === SCHEDULE_INDEX;

    const spots = useMemo(() => LeadQuiz.getBlindSpots(answers), [answers]);

    const setAnswer = (id, value) => setAnswers(current => ({ ...current, [id]: value }));

    const isAnswered = () => {
        if(!step) {
            return true;
        }
        let answer = answers[step.id];
        return step.kind === 'multi'
            ? Array.isArray(answer) && answer.length > 0
            : answer !== undefined && answer !== null;
    }

    const onToggle = optionId => {
        let current = answers[step.id] || [];
        setAnswer(step.id, current.includes(optionId)
            ? current.filter(id => id !== optionId)
            : current.concat(optionId));
    }

    /*
     * Nothing is flagged until the visitor tries to move on. A form that turns
     * red as each box is left is scolding somebody who was about to fill it in.
     * Once flagged, a field clears the moment it is right.
     */
    const problems = attempted ? Appointment.getCustomerProblems(customer) : {};

    const onFinish = next => {
        setConfirmation(next);
        LeadQuiz.clearSession();
    }

    /*
     * Sends the request, with the chosen time or without one. Without one it
     * is a call-back: the dealer rings to find a time instead.
     */
    const onSubmit = async appointment => {
        try {
            setError(null);
            setSending(appointment ? 'appointment' : 'callback');
            await Appointment.submit(utils, {
                appointment: appointment,
                customer: customer,
                dealership: dealership,
                form: 'lead_quiz',
                message: LeadQuiz.getSummary(answers)
            });
            onFinish(appointment ? { ...appointment, kind: 'requested' } : { kind: 'callback' });
        } catch(e) {
            setError(e.message);
        }
        setSending(false);
    }

    /*
     * Details, then a time, then one request.
     *
     * Nothing leaves the page until the visitor has either picked a time or
     * asked for a call, so going back to fix a typo cannot send a second copy.
     * A dealer with no open times skips the calendar and the details go
     * straight through as a call-back.
     *
     * With Calendly nothing is sent at all. Calendly's webhook creates the
     * lead on Global Data when the booking lands.
     */
    const onContinue = () => {
        setError(null);
        setAttempted(true);
        let found = Appointment.getCustomerProblems(customer);
        let first = ['firstName', 'lastName', 'email', 'phone', 'address'].find(key => found[key]);
        if(first) {
            document.getElementById(`quiz-field-${first}`)?.focus();
            return;
        }
        if(canSchedule === false) {
            onSubmit(null);
            return;
        }
        setIndex(SCHEDULE_INDEX);
    }

    const getChip = ({ active, label, onClick }) => (
        <div
        className={'pressable'}
        key={label}
        onClick={onClick}
        style={{
            alignItems: 'center',
            backgroundColor: active ? Theme.colors.primary() : Theme.colors.white,
            border: `1.5px solid ${active ? Theme.colors.primary() : Theme.colors.border()}`,
            borderRadius: 14,
            color: active ? Theme.colors.white : Theme.colors.text(),
            cursor: 'pointer',
            display: 'flex',
            fontSize: mobile ? 15 : 16,
            fontWeight: 500,
            gap: 12,
            padding: '15px 18px',
            transition: 'background-color 0.15s ease, border-color 0.15s ease'
        }}>
            <span style={{
                alignItems: 'center',
                border: `1.5px solid ${active ? Theme.colors.white : Theme.colors.border()}`,
                borderRadius: 999,
                display: 'flex',
                flexShrink: 0,
                height: 20,
                justifyContent: 'center',
                width: 20
            }}>
                {active && <Icon name={'check-lg'} size={11} color={Theme.colors.white} />}
            </span>
            {label}
        </div>
    );

    /*
     * The reveal.
     *
     * Appears the moment the question is answered rather than on the next
     * screen, because the answer is what earns it — this is the point where the
     * dealer would say "most people say the dryer, and here is why".
     */
    const getReveal = () => {
        if(isAnswered() === false) {
            return null;
        }
        return (
            <div
            className={'quiz-reveal'}
            key={step.id}
            style={{
                backgroundColor: Theme.tint(0.05),
                border: `1px solid ${Theme.colors.border()}`,
                borderRadius: 18,
                marginBottom: 24,
                padding: '18px 22px'
            }}>
                <div style={{ alignItems: 'center', display: 'flex', gap: 8, marginBottom: 8 }}>
                    <Icon name={'stars'} size={13} color={Theme.colors.primary()} />
                    <span style={Theme.textStyles.label({ color: Theme.colors.primary() })}>
                        {"What most people don't know"}
                    </span>
                </div>
                <div style={Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 15 })}>
                    {step.reveal(answers)}
                </div>
            </div>
        )
    }

    /*
     * Four hazards is a list, and set as prose the four disappear into the
     * middle of it - which is what the intro had become. Rendered as four rows
     * with one of them ticked, the argument is legible before it is read: the
     * visitor grants the tick on sight, and the three empty rows are the
     * question they came here with.
     *
     * The detail lines stay to one line each on purpose. Everything else is
     * taught inside the quiz by the per-step reveals, which is where it earns
     * the attention rather than spending it up front - the intro was repeating
     * those reveals and leaving the quiz with nothing left to say.
     */
    const HAZARDS = [{
        covered: true,
        note: 'Bedrooms and the hallway. This is the one you have.',
        title: 'Smoke'
    },{
        note: 'No smell, no warning. Furnace, water heater, a car in the garage.',
        title: 'Carbon monoxide'
    },{
        note: 'Kitchen, garage, attic, laundry. Rooms a smoke detector cannot sit in.',
        title: 'Heat'
    },{
        note: 'Behind the dishwasher or the water heater. You find out weeks later.',
        title: 'Water'
    }];

    const getHazard = hazard => (
        <div
        key={hazard.title}
        style={{
            alignItems: 'flex-start',
            borderTop: `1px solid ${Theme.colors.divider()}`,
            display: 'flex',
            gap: 14,
            padding: '13px 0'
        }}>
            <div style={{
                alignItems: 'center',
                backgroundColor: hazard.covered ? Theme.colors.primaryFill() : 'transparent',
                border: hazard.covered ? 'none' : `1.5px solid ${Theme.colors.lighterGrey}`,
                borderRadius: 11,
                display: 'flex',
                flexShrink: 0,
                height: 22,
                justifyContent: 'center',
                marginTop: 2,
                width: 22
            }}>
                <Icon
                color={hazard.covered ? Theme.colors.onPrimary() : Theme.colors.lighterGrey}
                name={hazard.covered ? 'check-lg' : 'dash-lg'}
                size={11} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={Theme.textStyles.body({
                    color: Theme.colors.text(),
                    fontSize: mobile ? 16 : 17,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    marginBottom: 2
                })}>{hazard.title}</div>
                <div style={Theme.textStyles.body({
                    color: Theme.colors.subText(),
                    fontSize: mobile ? 14 : 15,
                    lineHeight: 1.4,
                    marginBottom: 0
                })}>{hazard.note}</div>
            </div>
        </div>
    );

    const getIntro = () => (
        <>
            <span style={{ ...Theme.textStyles.label(), display: 'block', marginBottom: 12 }}>
                {'Free in-home safety evaluation'}
            </span>
            <h1 style={Theme.textStyles.heading({ marginBottom: 10 })}>
                {'Four things go wrong in a house'}
            </h1>
            <p style={Theme.textStyles.body({
                color: Theme.colors.subText(),
                fontSize: mobile ? 16 : 17,
                marginBottom: 22
            })}>
                {`Your detectors are built for one of them.`}
            </p>

            <div style={{
                borderBottom: `1px solid ${Theme.colors.divider()}`,
                marginBottom: 24
            }}>
                {HAZARDS.map(getHazard)}
            </div>

            <button
            className={'pressable'}
            onClick={() => setIndex(0)}
            style={{
                ...Theme.styles.button.primary(),
                fontSize: 17,
                padding: '16px 32px',
                ...mobile && { width: '100%' }
            }}>
                {'Check my house'}
            </button>
        </>
    );

    /*
     * Where am I, rather than how much is left. The right inset clears the
     * desktop close button; on a phone there is no close button and it was only
     * an asymmetric gutter.
     */
    const getProgress = () => (
        <div style={{ display: 'flex', gap: 6, paddingRight: mobile ? 0 : 32 }}>
            {steps.map((item, itemIndex) => (
                <div
                key={item.id}
                style={{
                    backgroundColor: itemIndex <= index ? Theme.colors.primaryFill() : Theme.colors.lighterGrey,
                    borderRadius: 2,
                    flexGrow: 1,
                    height: 3,
                    transition: 'background-color 0.3s ease'
                }} />
            ))}
        </div>
    );

    /*
     * The question frames the answers, so it stays put while they scroll.
     *
     * Hierarchy is built from size, weight and leading together rather than
     * size alone. At the old values the question was 18 and an answer was 16 at
     * the same weight, so the two competed and the eye had no obvious place to
     * start. The question now leads clearly, the supporting line sits under it,
     * and the answers are the quietest of the three - which is right, because
     * there are five of them and one of the question.
     *
     * Tracking is tightened only on the large text. Letters read too far apart
     * as they grow, and never at answer size.
     */
    const getStepHeader = () => (
        <>
            <div style={{ alignItems: 'center', display: 'flex', gap: 10, marginBottom: 10 }}>
                <Icon name={step.icon} size={15} color={Theme.colors.primary()} />
                <span style={Theme.textStyles.label()}>
                    {`Step ${index + 1} of ${steps.length} · ${step.eyebrow}`}
                </span>
            </div>
            <h2 style={{
                ...Theme.textStyles.heading({ fontSize: mobile ? 22 : 26 }),
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
                margin: '0 0 8px'
            }}>{step.question}</h2>
            {step.subtitle && (
                <p style={Theme.textStyles.body({
                    color: Theme.colors.subText(),
                    fontSize: mobile ? 15 : 16,
                    marginBottom: 0
                })}>{step.subtitle}</p>
            )}
        </>
    );

    const getStep = () => (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                {step.options.map(option => getChip({
                    active: step.kind === 'multi'
                        ? (answers[step.id] || []).includes(option.id)
                        : answers[step.id] === option.id,
                    label: option.label,
                    onClick: () => step.kind === 'multi' ? onToggle(option.id) : setAnswer(step.id, option.id)
                }))}
            </div>
            {getReveal()}
        </>
    );

    const getResultHeader = () => (
        <>
            <h2 style={{
                ...Theme.textStyles.heading({ fontSize: mobile ? 22 : 26 }),
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
                marginBottom: 8
            }}>
                {spots.length === 0
                    ? "You're better covered than most homes"
                    : `${spots.length} gap${spots.length === 1 ? '' : 's'} in your coverage`}
            </h2>
            <p style={Theme.textStyles.body({
                color: Theme.colors.subText(),
                fontSize: mobile ? 15 : 16,
                marginBottom: 0
            })}>
                {"These are the hazards your detectors don't catch, and the rooms they cannot sit in."}
            </p>
        </>
    );

    const getResult = () => (
        <>

            {/* A card each, and a leading icon at full strength on the same line
                as the thing it labels. The watermark bleeding off the corner was
                decoration with no job — it named nothing, and at 8% it was not
                legible as the object it depicted. This site uses icons to
                identify, never as texture, and proximity is the point: the icon
                sits beside what it describes. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, margin: '24px 0' }}>
                {spots.map((spot, i) => (
                    <div
                    className={'quiz-spot'}
                    key={i}
                    style={{
                        '--quiz-spot-index': i,
                        backgroundColor: Theme.colors.white,
                        border: `1px solid ${Theme.colors.border()}`,
                        borderRadius: 20,
                        boxShadow: Theme.boxShadow.card,
                        padding: mobile ? '20px 22px' : '24px 26px'
                    }}>
                        <div style={{ alignItems: 'flex-start', display: 'flex', gap: 12, marginBottom: 8 }}>
                            <Icon
                            color={Theme.colors.primary()}
                            name={spot.icon || 'slash-circle'}
                            size={18}
                            style={{ height: 20, marginTop: 3, width: 20 }} />
                            <div style={Theme.textStyles.subHeading({ fontSize: mobile ? 17 : 19 })}>
                                {spot.title}
                            </div>
                        </div>
                        <div style={{
                            ...Theme.textStyles.body({ color: Theme.colors.subText(), fontSize: 15 }),
                            paddingLeft: 32
                        }}>
                            {spot.body}
                        </div>
                    </div>
                ))}
            </div>

            <p style={Theme.textStyles.body({ color: Theme.colors.subText(), marginBottom: 18 })}>
                {`Five questions can only tell you so much. Someone from ${dealerName} will come out and go through the house with you. They'll look at the layout, show you what's missing, and tell you what it would take to cover it. The inspection is free and there is no obligation to purchase anything.`}
            </p>
        </>
    );

    // red stays red while focused, so the ring is red too rather than the
    // brand color painting over the one thing the visitor needs to see
    const getInputStyle = key => ({
        '--quiz-focus-border': problems[key] ? Theme.colors.error : Theme.colors.primary(),
        '--quiz-focus-ring': problems[key] ? Theme.tint(0.18, Theme.colors.error) : Theme.tint(0.18),
        border: `1.5px solid ${problems[key] ? Theme.colors.error : Theme.colors.border()}`,
        borderRadius: 12,
        fontFamily: 'inherit',
        fontSize: 16,
        padding: '14px 16px',
        width: '100%'
    });

    // only a reason that is not obvious from an empty box gets a sentence
    const getHint = key => problems[key] && problems[key] !== 'missing' ? problems[key] : null;

    const getField = (key, label, type = 'text') => (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
            <span style={Theme.textStyles.label()}>{label}</span>
            <input
            aria-invalid={problems[key] ? true : undefined}
            autoComplete={AUTOCOMPLETE[key]}
            className={'quiz-input'}
            id={`quiz-field-${key}`}
            inputMode={type === 'tel' ? 'tel' : undefined}
            name={AUTOCOMPLETE[key]}
            onChange={event => setCustomer(current => ({ ...current, [key]: event.target.value }))}
            style={getInputStyle(key)}
            type={type}
            value={customer[key]} />
            {getHint(key) && (
                <span style={Theme.textStyles.caption({ color: Theme.colors.error })}>{getHint(key)}</span>
            )}
        </label>
    );

    const getPinnedHeader = (title, subtitle) => (
        <>
            <h2 style={{
                ...Theme.textStyles.heading({ fontSize: mobile ? 22 : 26 }),
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
                marginBottom: 8
            }}>{title}</h2>
            <p style={Theme.textStyles.body({
                color: Theme.colors.subText(),
                fontSize: mobile ? 15 : 16,
                marginBottom: 0
            })}>{subtitle}</p>
        </>
    );

    const getFormHeader = () => getPinnedHeader(
        'Book your free evaluation',
        canSchedule
            ? `We need to know who to call and where your home is. After that, you'll pick a day and time you'd prefer.`
            : `We need to know who to call and where your home is. Someone from ${dealerName} will call you to set up a time.`
    );

    const getScheduleHeader = () => getPinnedHeader(
        'Book An Evaluation',
        calendly
            ? `Pick the day and time that works best for you, then fill in your contact details and address. We'll call you to confirm everything before anyone comes out.`
            : `Pick the day and time you'd prefer. We'll call you to confirm the date, time, and address before anyone comes out.`
    );

    const getError = () => error && (
        <div style={Theme.textStyles.body({ color: Theme.colors.error, marginBottom: 16 })}>{error}</div>
    );

    const getForm = () => (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
                <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', gap: 16 }}>
                    {getField('firstName', 'First name')}
                    {getField('lastName', 'Last name')}
                </div>
                {getField('email', 'Email address', 'email')}
                {getField('phone', 'Phone number', 'tel')}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                    <label htmlFor={'quiz-field-address'} style={Theme.textStyles.label()}>{'Home address'}</label>
                    <AddressLookup
                    className={'quiz-input'}
                    hint={getHint('address')}
                    id={'quiz-field-address'}
                    inputStyle={getInputStyle('address')}
                    invalid={problems.address ? true : false}
                    onChange={address => setCustomer(current => ({ ...current, address }))}
                    utils={utils}
                    value={customer.address} />
                </div>
            </div>
            {getError()}
        </>
    );

    const getSchedule = () => {
        if(calendly) {
            return (
                <CalendlyScheduler
                config={calendly}
                form={'lead_quiz'}
                notes={LeadQuiz.getSummary(answers)}
                onScheduled={() => onFinish({ kind: 'calendly' })} />
            )
        }
        return (
            <>
                <AppointmentPicker
                days={days}
                onSelect={setSelected}
                selected={selected}
                timezone={Appointment.getTimezone(dealership)} />
                {getError()}
            </>
        )
    };

    /*
     * What happened, in the words the visitor needs next. A chosen time is
     * repeated back to them because it is the one detail they will want to
     * check before they close this. It is a request rather than a booking
     * until the dealer confirms it, and the copy says so.
     */
    const getConfirmation = () => {
        let content = {
            requested: {
                body: `You asked for ${moment(`${confirmation.date} ${confirmation.time}`, 'YYYY-MM-DD HH:mm:ss').format('dddd, MMMM D [at] h:mm A')}. Someone from ${dealerName} will call you to confirm the date, time, and address before anyone comes out to your home.`,
                title: "We've got your request"
            },
            calendly: {
                body: `You'll get an email shortly with the details. Someone from ${dealerName} will also call you to confirm the date, time, and address before anyone comes out.`,
                title: "We've got your booking"
            },
            callback: {
                body: `Someone from ${dealerName} will give you a call soon to find a time that works for you.`,
                title: "We've got your request"
            }
        }[confirmation.kind];
        return (
            <div style={{ textAlign: 'center' }}>
                <Icon name={'check-circle-fill'} size={44} color={Theme.colors.primary()} />
                <h2 style={Theme.textStyles.heading({ margin: '20px 0 12px' })}>{content.title}</h2>
                <p style={Theme.textStyles.body({ color: Theme.colors.subText() })}>{content.body}</p>
            </div>
        )
    };

    /*
     * One row, the same row on every screen. Back and Start over are both ways
     * of going backwards, so they belong together. On a phone everything is full
     * width and stacked - a thumb reaching for the next thing should not aim.
     */
    const getNavigation = () => {

        if(index < 0) {
            return null;
        }

        if(confirmation) {
            if(typeof onClose !== 'function') {
                return null;
            }
            return (
                <div style={{ display: 'flex', justifyContent: mobile ? 'stretch' : 'flex-end' }}>
                    <button
                    className={'pressable'}
                    onClick={onClose}
                    style={{
                        ...Theme.styles.button.primary(),
                        padding: '14px 28px',
                        ...mobile && { width: '100%' }
                    }}>
                        {'Done'}
                    </button>
                </div>
            )
        }

        const getPrimary = () => {
            if(showingSchedule) {

                // Calendly carries its own confirm button inside the embed
                if(calendly) {
                    return null;
                }
                return {
                    disabled: sending || !selected?.time,
                    label: sending === 'appointment' ? 'Sending…' : 'Request this appointment',
                    onClick: () => onSubmit(selected)
                };
            }
            if(showingForm) {
                return {
                    disabled: sending,
                    label: sending ? 'Sending…' : (canSchedule ? 'Continue' : 'Request my free evaluation'),
                    onClick: onContinue
                };
            }
            return {
                disabled: isAnswered() === false,
                label: showingResult ? 'Book my free evaluation' : 'Next',

                // Calendly asks for the contact details and address itself
                onClick: () => setIndex(current => showingResult && calendly ? SCHEDULE_INDEX : current + 1)
            };
        };

        let primary = getPrimary();

        return (
            <div style={{
                alignItems: mobile ? 'stretch' : 'center',
                display: 'flex',
                flexDirection: mobile ? 'column-reverse' : 'row',
                gap: mobile ? 8 : 12,
                justifyContent: 'space-between'
            }}>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: mobile ? 'column' : 'row',
                    gap: mobile ? 2 : 8,
                    width: mobile ? '100%' : 'auto'
                }}>
                    <button
                    className={'pressable'}
                    onClick={() => { setError(null); setIndex(current => showingSchedule && calendly ? steps.length : current - 1); }}
                    style={{
                        ...Theme.styles.button.secondary(),
                        flexShrink: 0,
                        padding: '14px 24px',
                        ...mobile && { width: '100%' }
                    }}>
                        {'Back'}
                    </button>
                    {Object.keys(answers).length > 0 && (
                        <button
                        className={'pressable'}
                        onClick={() => {
                            LeadQuiz.clearSession();
                            setAnswers({});
                            setAttempted(false);
                            setError(null);
                            setSelected(null);
                            setIndex(0);
                        }}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '12px',
                            ...Theme.textStyles.caption({ color: Theme.colors.subText() }),
                            ...mobile && { width: '100%' }
                        }}>
                            {'Start over'}
                        </button>
                    )}
                </div>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: mobile ? 'column-reverse' : 'row',
                    gap: mobile ? 8 : 12,
                    width: mobile ? '100%' : 'auto'
                }}>

                    {/* the way out of the calendar is a real choice, so it is a
                        real button - quieter than the one we would rather
                        they press, but not hidden in a line of small text */}
                    {showingSchedule && !calendly && (
                        <button
                        className={'pressable'}
                        disabled={sending}
                        onClick={() => onSubmit(null)}
                        style={{
                            ...Theme.styles.button.primary(),
                            backgroundColor: Theme.colors.white,
                            border: `1.5px solid ${Theme.colors.border()}`,
                            color: Theme.colors.text(),
                            opacity: sending ? 0.5 : 1,
                            padding: '14px 24px',
                            ...mobile && { width: '100%' }
                        }}>
                            {sending === 'callback' ? 'Sending…' : 'Call me to find a time'}
                        </button>
                    )}
                    {primary && (
                        <button
                        className={'pressable'}
                        disabled={primary.disabled}
                        onClick={primary.onClick}
                        style={{
                            ...Theme.styles.button.primary(),
                            opacity: primary.disabled ? 0.5 : 1,
                            padding: '14px 28px',
                            ...mobile && { width: '100%' }
                        }}>
                            {primary.label}
                        </button>
                    )}
                </div>
            </div>
        )
    };

    /*
     * The buttons do not scroll away.
     *
     * With the whole modal scrolling, every step ended with a scroll down to
     * find Next and a scroll back up to read the next question. The progress and
     * the actions are chrome — they belong to the container, and only the
     * question moves.
     */
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            margin: '0 auto',
            maxHeight: '100%',
            maxWidth: 620,
            minHeight: 0,
            width: '100%'
        }}>
            {/* Pinned. The progress and whatever frames the current screen -
                the question, the results heading - stay where they are, and
                only the answers move. Letting the question scroll away meant
                reading it, scrolling to the answers, and scrolling back up to
                check what was being asked. */}
            {index >= 0 && (
                <div style={{
                    borderBottom: calendly ? null : `1px solid ${Theme.colors.border()}`,
                    flexShrink: 0,
                    paddingBottom: mobile ? 16 : 20
                }}>
                    <div style={{ paddingBottom: mobile ? 18 : 22 }}>
                        {getProgress()}
                    </div>
                    {!confirmation && (
                        <>
                            {step && getStepHeader()}
                            {showingResult && getResultHeader()}
                            {showingForm && getFormHeader()}
                            {showingSchedule && getScheduleHeader()}
                        </>
                    )}
                </div>
            )}

            <div
            ref={scrollRef}
            style={{
                WebkitOverflowScrolling: 'touch',
                flex: 1,
                marginLeft: -SCROLL_GUTTER,
                marginRight: -SCROLL_GUTTER,
                minHeight: 0,
                overflowY: 'auto',
                paddingBottom: SCROLL_GUTTER,
                paddingLeft: SCROLL_GUTTER,
                paddingRight: SCROLL_GUTTER,
                paddingTop: index < 0 ? 0 : (mobile ? 18 : 22) - SCROLL_GUTTER
            }}>
                {confirmation ? getConfirmation() : (
                    <>
                        {index < 0 && getIntro()}
                        {step && getStep()}
                        {showingResult && getResult()}
                        {showingForm && getForm()}
                        {showingSchedule && getSchedule()}
                    </>
                )}
            </div>

            <div style={{
                backgroundColor: Theme.colors.white,
                borderTop: index < 0 || calendly ? 'none' : `1px solid ${Theme.colors.border()}`,
                flexShrink: 0,
                paddingTop: index < 0 ? 0 : 18
            }}>
                {getNavigation()}
            </div>
        </div>
    )
}

export default LeadQuizView;
